// Livraisons (E2, E3, E4) : suivi des missions en cours sur carte et liste avec l'âge de la position, exceptions
// d'abord (retards, arrêts prolongés, positions anciennes) ; historique des ordres ; fiche d'un ordre (étapes,
// chargement, réceptions, bons PDF, canal de service, affectation, annulation) ; livraisons attendues par la
// station ou l'établissement et parcours de réception (huit étapes, D-019, D-020), utilisable hors connexion.

import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { LIBELLES_ETATS_ORDRE, TYPES_OPERATIONS_LOT3, deProduit, type EtatOrdre } from '@carburanttrack/shared/logistique';
import { api, messageErreur } from '../../api/client';
import { aUnRole, useSession } from '../../app/session';
import { CarteProvinces, type PointCarte } from '../../composants/CarteProvinces';
import { Indisponible, Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { useHorsLigne } from '../../horsligne/contexte';
import { useCarte } from '../../api/requetes';
import { maintenantLocal } from '../stocks/composants';
import { AfficherPlus, AgePosition, arrivee, EtiquetteEtatOrdre, EtiquetteException, exceptionMission, libelleEtape, LiensBons, quantite, resumeOrdre, usePaquet, volumeOrdre } from './communs';
import { nomProduit } from './Station';
import type { FicheOrdre, LigneOrdre, PaquetTerrain, SuiviMissions } from './types';

const ROLES_DESTINATAIRES = ['station', 'etablissement_prioritaire'];

/** Page « Livraisons » : livraisons à réceptionner pour un destinataire, suivi des missions pour les autres. */
export function PageLivraisons() {
  const { etat } = useSession();
  if (aUnRole(etat, ...ROLES_DESTINATAIRES)) return <LivraisonsAttendues />;
  return <SuiviLivraisons />;
}

// --- Suivi des missions en cours -------------------------------------------------------------------------------

function SuiviLivraisons() {
  const [vue, setVue] = useState<'en_cours' | 'historique'>('en_cours');
  const suivi = useQuery({ queryKey: ['suivi-missions'], queryFn: async () => (await api<SuiviMissions>('/api/v1/suivi/missions')).donnees, refetchInterval: 120_000 });
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Livraisons</h1>
          <p className="attenue">Missions en cours, exceptions d’abord. L’âge de chaque position est affiché ; une position déclarée par le conducteur est distinguée de celle de l’appareil.</p>
        </div>
        <div className="segments" role="tablist" aria-label="Livraisons affichées">
          <button type="button" role="tab" aria-selected={vue === 'en_cours'} onClick={() => setVue('en_cours')}>
            En cours
          </button>
          <button type="button" role="tab" aria-selected={vue === 'historique'} onClick={() => setVue('historique')}>
            Historique
          </button>
        </div>
      </div>
      {vue === 'en_cours' ? (
        <>
          {suivi.isLoading && <Squelette lignes={6} libelle="Chargement des missions en cours" />}
          {suivi.error && <MessageErreur erreur={suivi.error} titre="Missions indisponibles" />}
          {suivi.data && <MissionsEnCours s={suivi.data} />}
        </>
      ) : (
        <HistoriqueOrdres />
      )}
    </div>
  );
}

function MissionsEnCours({ s }: { s: SuiviMissions }) {
  const carte = useCarte();
  const [choisie, setChoisie] = useState<string | null>(null);
  // Liste complète paginée : vingt missions, puis vingt de plus à la demande (les exceptions restent toutes visibles).
  const [nbMissions, setNbMissions] = useState(20);
  const exceptions = s.missions.filter((m) => exceptionMission(m));
  const normales = s.missions.filter((m) => !exceptionMission(m));
  const points: PointCarte[] = s.missions
    .filter((m) => m.position && m.position.longitude !== null && m.position.latitude !== null && m.etat !== 'emis')
    .map((m) => ({ id: m.id, nom: `${m.numero}, ${m.position!.libelle}`, longitude: m.position!.longitude!, latitude: m.position!.latitude!, categorie: 'mission', ton: exceptionMission(m)?.ton ?? null }));
  const sansPosition = s.missions.filter((m) => m.etat !== 'emis' && (!m.position || m.position.longitude === null)).length;
  return (
    <>
      <div className="compteurs" role="list" aria-label="Compteurs des missions">
        <div role="listitem">
          <b>{s.compteurs.total}</b>missions en cours
        </div>
        <div role="listitem">
          <b>{s.compteurs.enRoute}</b>en route
        </div>
        <div role="listitem">
          <b>{s.compteurs.aCharger}</b>à charger
        </div>
        <div role="listitem">
          <b className={s.compteurs.enRetard ? 'ambre' : ''}>{s.compteurs.enRetard}</b>en retard
        </div>
        <div role="listitem">
          <b className={s.compteurs.arretsProlonges ? 'ambre' : ''}>{s.compteurs.arretsProlonges}</b>arrêts prolongés
        </div>
        <div role="listitem">
          <b className={s.compteurs.positionsAnciennes ? 'ambre' : ''}>{s.compteurs.positionsAnciennes}</b>positions anciennes ou inconnues
        </div>
      </div>
      <div className="deux-colonnes">
        <div className="principal">
          <section className="carte-bloc" aria-labelledby="titre-exceptions-missions">
            <div className="carte-bloc-tete">
              <h2 id="titre-exceptions-missions">À surveiller</h2>
              <span className="attenue">Calculé le {dateHeure(s.maintenant)}</span>
            </div>
            {exceptions.length === 0 ? (
              <p className="attenue">Aucune mission en retard, arrêtée ou sans position récente.</p>
            ) : (
              <ol className="liste-exceptions">
                {exceptions.map((m, i) => {
                  const e = exceptionMission(m)!;
                  return (
                    <li key={m.id}>
                      <Link className={`exception ${e.ton}`} to={`/livraisons/${m.id}`} onMouseEnter={() => setChoisie(m.id)}>
                        <span className="numero" aria-hidden="true">
                          {i + 1}
                        </span>
                        <span className="texte">
                          <span className="marque-exception">{e.libelle}</span>
                          <b>
                            {m.numero}, {resumeOrdre(m)}
                          </b>
                          <small>
                            {m.transporteur.nom}
                            {m.vehicule ? `, ${m.vehicule.immatriculation}` : ''} ; <AgePosition ligne={m} /> ; {arrivee(m)}
                          </small>
                        </span>
                        <Icone nom="chevron" />
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
          <section className="carte-bloc" aria-labelledby="titre-liste-missions">
            <div className="carte-bloc-tete">
              <h2 id="titre-liste-missions">Toutes les missions en cours</h2>
              <span className="attenue">
                {normales.length} sans exception sur {s.missions.length}
              </span>
            </div>
            <TableauOrdres lignes={s.missions.slice(0, nbMissions)} avecPosition legende="Missions en cours" />
            <AfficherPlus affiches={Math.min(nbMissions, s.missions.length)} total={s.missions.length} pas={20} libelle="missions" onPlus={() => setNbMissions((n) => n + 20)} />
          </section>
        </div>
        <aside className="lateral" aria-label="Carte des missions">
          <section className="carte-bloc">
            <h2>Carte</h2>
            {carte.data ? (
              <CarteProvinces carte={carte.data} points={points} pointMisEnAvant={choisie} titre="Dernières positions connues des missions en cours" libellePoints="Mission (dernière position)" mode="exceptions" />
            ) : carte.error ? (
              <Indisponible motif="tracé des provinces non chargé" />
            ) : (
              <Squelette lignes={4} />
            )}
            <p className="attenue">
              Un point est une dernière position connue, jamais une position actuelle. {sansPosition > 0 ? `${sansPosition} mission${sansPosition > 1 ? 's' : ''} en route sans position lisible n’apparai${sansPosition > 1 ? 'ssent' : 't'} pas sur la carte.` : ''}
            </p>
          </section>
        </aside>
      </div>
    </>
  );
}

export function TableauOrdres({ lignes, avecPosition, legende, lienBase = '/livraisons' }: { lignes: LigneOrdre[]; avecPosition?: boolean; legende: string; lienBase?: string }) {
  if (lignes.length === 0) return <p className="attenue">Aucun ordre de transport.</p>;
  return (
    <div className="tableau-conteneur">
      <table className="tableau">
        <caption className="visuellement-cache">{legende}</caption>
        <thead>
          <tr>
            <th scope="col">Ordre</th>
            <th scope="col">État</th>
            <th scope="col" className="nombre">
              Volume
            </th>
            <th scope="col" className="masquable">
              Transporteur
            </th>
            {avecPosition && <th scope="col">Dernière position</th>}
            <th scope="col" className="masquable">
              Arrivée
            </th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => {
            const e = exceptionMission(l);
            return (
              <tr key={l.id} className={e ? 'exception-ligne' : undefined}>
                <td>
                  <Link className="lien-ligne" to={`${lienBase}/${l.id}`}>
                    {l.numero}
                  </Link>
                  <small>
                    {l.produitNom}, {l.origine.nom} vers {l.destination.nom}
                  </small>
                </td>
                <td>
                  <EtiquetteEtatOrdre etat={l.etat} libelle={l.etatLibelle} /> <EtiquetteException exception={e} />
                </td>
                <td className="nombre">
                  {volumeOrdre(l)}
                  <small>{l.quantiteChargee ? 'chargé' : 'prévu'}</small>
                </td>
                <td className="masquable">
                  {l.transporteur.nom}
                  <small>{l.vehicule?.immatriculation ?? 'véhicule à affecter'}</small>
                </td>
                {avecPosition && (
                  <td>
                    <AgePosition ligne={l} />
                  </td>
                )}
                <td className="masquable">{l.dateArrivee ? dateHeure(l.dateArrivee) : <span className="attenue">prévue {dateHeure(l.dateArriveePrevue)}</span>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ETATS_FILTRE: EtatOrdre[] = ['recu', 'solde', 'recu_partiellement', 'en_rapprochement', 'annule'];

function HistoriqueOrdres() {
  const [etat, setEtat] = useState<string>('');
  const [recherche, setRecherche] = useState('');
  const [limite, setLimite] = useState(100);
  const q = useQuery({
    queryKey: ['ordres-transport', etat, recherche, limite],
    queryFn: async () => (await api<LigneOrdre[]>(`/api/v1/ordres-transport?limite=${limite}${etat ? `&etat=${etat}` : ''}${recherche.trim() ? `&q=${encodeURIComponent(recherche.trim())}` : ''}`)).donnees,
  });
  return (
    <section className="carte-bloc" aria-labelledby="titre-historique-ordres">
      <div className="carte-bloc-tete">
        <h2 id="titre-historique-ordres">Ordres de transport</h2>
        <span className="attenue">Filtrés par le serveur selon vos habilitations</span>
      </div>
      <div className="barre-filtres">
        <div className="champ champ-etroit">
          <label htmlFor="h-etat">État</label>
          <select id="h-etat" value={etat} onChange={(e) => setEtat(e.target.value)}>
            <option value="">Tous</option>
            {ETATS_FILTRE.map((e) => (
              <option key={e} value={e}>
                {LIBELLES_ETATS_ORDRE[e]}
              </option>
            ))}
          </select>
        </div>
        <div className="champ champ-etroit">
          <label htmlFor="h-numero">Numéro</label>
          <input id="h-numero" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="OT-2026" maxLength={40} />
        </div>
      </div>
      {q.isLoading && <Squelette lignes={5} />}
      {q.error && <MessageErreur erreur={q.error} />}
      {q.data && <TableauOrdres lignes={q.data} legende="Historique des ordres de transport" />}
      {q.data && q.data.length >= limite && (
        <button type="button" className="bouton espace-haut" onClick={() => setLimite(limite + 100)}>
          Afficher 100 ordres de plus
        </button>
      )}
    </section>
  );
}

// --- Fiche d'un ordre ------------------------------------------------------------------------------------------

export function PageFicheOrdre() {
  const { id } = useParams();
  const q = useQuery({ queryKey: ['ordre-transport', id], queryFn: async () => (await api<FicheOrdre>(`/api/v1/ordres-transport/${id}`)).donnees, enabled: Boolean(id) });
  if (q.isLoading) return <Squelette lignes={8} libelle="Chargement de l’ordre de transport" />;
  if (q.error || !q.data) return <MessageErreur erreur={q.error} titre="Ordre de transport indisponible" />;
  return <FicheOrdreVue o={q.data} />;
}

function FicheOrdreVue({ o }: { o: FicheOrdre }) {
  const e = exceptionMission(o);
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">
            {o.proprietaire.nom}, transporteur {o.transporteur.nom}
          </span>
          <h1>
            {o.numero} <EtiquetteEtatOrdre etat={o.etat} libelle={o.etatLibelle} /> <EtiquetteException exception={e} />
          </h1>
          <p className="attenue">{resumeOrdre(o)}</p>
        </div>
        <div className="actions">
          {o.droits.receptionner && ['charge', 'en_route', 'arrive', 'recu_partiellement'].includes(o.etat) && (
            <Link className="bouton primaire" to={`/livraisons/${o.id}/reception`}>
              Confirmer la réception
            </Link>
          )}
          {o.droits.confirmerChargement && o.etat === 'emis' && (
            <Link className="bouton primaire" to={`/chargements/${o.id}`}>
              Confirmer le chargement
            </Link>
          )}
          {o.droits.etapes && ['charge', 'en_route', 'arrive'].includes(o.etat) && (
            <Link className="bouton" to={`/missions/${o.id}`}>
              Étapes de la mission
            </Link>
          )}
          {o.canalMessagerieId && (
            <Link className="bouton" to={`/messages/${o.canalMessagerieId}`}>
              Canal de la livraison
            </Link>
          )}
        </div>
      </div>
      <div className="deux-colonnes">
        <div className="principal">
          <section className="carte-bloc" aria-labelledby="titre-ordre">
            <h2 id="titre-ordre">Ordre de transport</h2>
            <dl className="liste-definitions">
              <dt>Produit et volume prévu</dt>
              <dd>
                {quantite(o.volumePrevu, o.unite)} {deProduit(o.produitCode)}
                {o.prioritaire ? ', prioritaire' : ''}
              </dd>
              <dt>Chargement</dt>
              <dd>
                {o.origine.nom} ({o.expediteur.nom}), prévu {dateHeure(o.dateChargementPrevue)}
                {o.dateChargement ? `, chargé ${dateHeure(o.dateChargement)}` : ''}
              </dd>
              <dt>Destination</dt>
              <dd>
                {o.destination.nom} ({o.destinataire.nom}), {arrivee(o)}
              </dd>
              <dt>Véhicule</dt>
              <dd>{o.vehiculeDetail ? `${o.vehiculeDetail.immatriculation}, ${quantite(o.vehiculeDetail.capacite, 'L')}, ${o.vehiculeDetail.compartiments} compartiment${o.vehiculeDetail.compartiments > 1 ? 's' : ''}` : <span className="attenue">à affecter par le transporteur</span>}</dd>
              <dt>Conducteur</dt>
              <dd>
                {o.conducteurDetail ? (
                  <>
                    {o.conducteurDetail.nom}
                    {o.conducteurDetail.telephone ? `, ${o.conducteurDetail.telephone}` : null}
                    {!o.conducteurDetail.telephone && o.conducteurDetail.motifTelephoneMasque ? <span className="attenue"> (téléphone indisponible : {o.conducteurDetail.motifTelephoneMasque.toLowerCase()})</span> : null}
                  </>
                ) : (
                  <span className="attenue">à affecter par le transporteur</span>
                )}
              </dd>
              <dt>Quantité chargée</dt>
              <dd>{o.quantiteChargee ? quantite(o.quantiteChargee, o.unite) : <span className="attenue">indisponible : chargement non confirmé par le dépôt</span>}</dd>
              <dt>Quantité reçue</dt>
              <dd>{Number(o.quantiteRecue) > 0 ? quantite(o.quantiteRecue, o.unite) : <span className="attenue">aucune réception validée</span>}</dd>
              <dt>Scellés</dt>
              <dd>{o.scelles.length ? o.scelles.join(', ') : <span className="attenue">aucun scellé déclaré</span>}</dd>
              <dt>Documents</dt>
              <dd>
                <LiensBons id={o.id} numero={o.numero} bonChargement={o.droits.bonChargement} bonLivraison={o.droits.bonLivraison} />
              </dd>
            </dl>
            {o.compartiments.length > 0 && (
              <div className="tableau-conteneur espace-haut">
                <table className="tableau">
                  <caption className="visuellement-cache">Compartiments</caption>
                  <thead>
                    <tr>
                      <th scope="col">Compartiment</th>
                      <th scope="col" className="nombre">
                        Prévu
                      </th>
                      <th scope="col" className="nombre">
                        Chargé
                      </th>
                      <th scope="col">Scellé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.compartiments.map((c) => (
                      <tr key={c.numero}>
                        <td>{c.numero}</td>
                        <td className="nombre">{quantite(c.volumePrevu, o.unite)}</td>
                        <td className="nombre">{c.volumeCharge ? quantite(c.volumeCharge, o.unite) : <span className="cellule-indisponible">indisponible</span>}</td>
                        <td>{c.scelle ?? <span className="attenue">aucun</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <ReceptionsOrdre o={o} />
          {o.rapprochements.length > 0 && (
            <section className="carte-bloc" aria-labelledby="titre-dossiers">
              <h2 id="titre-dossiers">Rapprochement</h2>
              <ul className="liste-lignes">
                {o.rapprochements.map((r) => (
                  <li key={r}>
                    <Link to={`/rapprochements/${r}`}>
                      <span>
                        <b>Dossier de rapprochement</b>
                        <small>Observations des parties, justificatifs, clôture motivée</small>
                      </span>
                      <Icone nom="chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {o.annulation && (
            <Message type="info">
              Ordre annulé le {dateHeure(o.annulation.le)} : {o.annulation.motif ?? 'motif non renseigné'}.{' '}
              {o.annulation.mouvementRetourId ? 'Le produit chargé a fait l’objet d’un mouvement de retour au dépôt (R-20).' : 'Annulé avant chargement : la réservation a été levée.'}
            </Message>
          )}
          {o.droits.affecter && ['emis', 'charge'].includes(o.etat) && <AffectationOrdre o={o} />}
          {o.droits.annuler && !['annule', 'recu', 'solde', 'en_rapprochement'].includes(o.etat) && <AnnulationOrdre o={o} />}
        </div>
        <aside className="lateral">
          <section className="carte-bloc" aria-labelledby="titre-etapes">
            <h2 id="titre-etapes">Étapes</h2>
            <p className="attenue">
              Dernière position : <AgePosition ligne={o} />
            </p>
            {o.etapes.length === 0 ? (
              <p className="attenue">Aucune étape déclarée.</p>
            ) : (
              <ol className="liste-lignes">
                {o.etapes.map((x) => (
                  <li key={x.id}>
                    <span>
                      <b>
                        {libelleEtape(x.type)}
                        {x.lieu ? `, ${x.lieu}` : ''}
                      </b>
                      <small>
                        {dateHeure(x.horodatageEvenement)}
                        {x.horodatageReception !== x.horodatageEvenement && Date.parse(x.horodatageReception) - Date.parse(x.horodatageEvenement) > 300_000 ? `, reçue ${dateHeure(x.horodatageReception)}` : ''}
                        {x.source === 'hors_ligne' ? ', saisie hors connexion' : ''}
                        {x.position ? `, position ${x.position.source === 'appareil' ? 'de l’appareil' : 'déclarée'}${x.position.precisionMetres ? ` (précision ${formaterNombre(String(x.position.precisionMetres))} m)` : ''}` : x.positionPurgee ? `, coordonnées supprimées après ${formaterNombre(String(o.conservationPositionsJours))} jours (durée de conservation)` : ', sans position'}
                        {x.apresConflit ? ', retenue après résolution d’un conflit' : ''}
                      </small>
                      {x.commentaire && <small>{x.commentaire}</small>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

function ReceptionsOrdre({ o }: { o: FicheOrdre }) {
  const client = useQueryClient();
  const [motif, setMotif] = useState('');
  const [cible, setCible] = useState<{ id: string; action: 'valider' | 'rejeter' | 'contester' } | null>(null);
  const action = useMutation({
    mutationFn: async () => {
      if (!cible) return;
      if (cible.action === 'contester') await api(`/api/v1/receptions/${cible.id}/contestation`, { corps: { motif } });
      else await api(`/api/v1/receptions/${cible.id}/validation`, { corps: { decision: cible.action, motif } });
    },
    onSuccess: () => {
      setCible(null);
      setMotif('');
      void client.invalidateQueries({ queryKey: ['ordre-transport', o.id] });
    },
  });
  if (o.receptions === null) return <Indisponible motif={o.motifReceptionsMasquees ?? 'réceptions non lisibles'} />;
  if (o.receptions.length === 0) return null;
  return (
    <section className="carte-bloc" aria-labelledby="titre-receptions">
      <h2 id="titre-receptions">Réceptions</h2>
      <ul className="liste-lignes">
        {o.receptions.map((r) => (
          <li key={r.id}>
            <span>
              <b>
                Réception {r.rang} : {quantite(r.quantite, o.unite)} {r.partielle ? '(partielle)' : ''} <span className={`etiquette ${r.etat === 'validee' ? 'vert' : r.etat === 'en_attente_validation' ? 'ambre' : 'gris'}`}>{r.etatLibelle}</span>
              </b>
              <small>
                {dateHeure(r.horodatage)}
                {r.ecart !== null ? `, écart ${quantite(r.ecart, o.unite)} (${formaterNombre(r.ecartPourcentage ?? '0', 2)} %)` : ''}
                {r.dansTolerance === true ? ', dans la tolérance' : r.dansTolerance === false ? ', au-delà de la tolérance' : ''}
                {r.toleranceAppliquee ? ` de ${quantite(r.toleranceAppliquee, o.unite)}` : ''}
                {r.reliquat && Number(r.reliquat) > 0 ? `, reliquat ${quantite(r.reliquat, o.unite)} attendu avant le ${dateHeure(r.expirationReliquat)}` : ''}
              </small>
              {r.motifValidation && <small>Motif : {r.motifValidation}</small>}
            </span>
            <span className="actions">
              {r.etat === 'en_attente_validation' && o.droits.receptionner && (
                <>
                  <button type="button" className="bouton" onClick={() => setCible({ id: r.id, action: 'valider' })}>
                    Valider
                  </button>
                  <button type="button" className="bouton" onClick={() => setCible({ id: r.id, action: 'rejeter' })}>
                    Rejeter
                  </button>
                </>
              )}
              {r.etat === 'validee' && (
                <button type="button" className="bouton" onClick={() => setCible({ id: r.id, action: 'contester' })}>
                  Contester
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {cible && (
        <form
          className="formulaire-ligne"
          onSubmit={(e) => {
            e.preventDefault();
            action.mutate();
          }}
        >
          <div className="champ">
            <label htmlFor="r-motif">{cible.action === 'contester' ? 'Motif de la contestation (R-16)' : cible.action === 'valider' ? 'Motif de la validation' : 'Motif du rejet'}</label>
            <input id="r-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} maxLength={500} />
          </div>
          <div className="actions">
            <button type="submit" className="bouton primaire" disabled={action.isPending || motif.trim().length < 3}>
              {cible.action === 'contester' ? 'Contester la réception' : cible.action === 'valider' ? 'Valider la réception' : 'Rejeter la réception'}
            </button>
            <button type="button" className="bouton" onClick={() => setCible(null)}>
              Annuler
            </button>
          </div>
          {action.error && <Message type="erreur">{messageErreur(action.error)}</Message>}
        </form>
      )}
    </section>
  );
}

interface FlotteAffectation {
  vehicules: { id: string; immatriculation: string; capacite: string; compartiments: number; engage: boolean }[];
  conducteurs: { id: string; nom: string }[];
}

function AffectationOrdre({ o }: { o: FicheOrdre }) {
  const { etat } = useSession();
  const client = useQueryClient();
  const estTransporteur = aUnRole(etat, 'transporteur');
  const flotte = useQuery({
    queryKey: ['affectation-flotte', o.transporteur.id, estTransporteur],
    queryFn: async (): Promise<FlotteAffectation> => {
      if (estTransporteur) {
        const f = (await api<{ vehicules: { id: string; immatriculation: string; capacite: string; compartiments: number; activite: string }[]; conducteurs: { id: string; nom: string }[] }>('/api/v1/transport/flotte')).donnees;
        return { vehicules: f.vehicules.map((v) => ({ ...v, engage: v.activite !== 'disponible' })), conducteurs: f.conducteurs };
      }
      const r = (await api<{ transporteurs: { id: string; vehicules: FlotteAffectation['vehicules']; conducteurs: FlotteAffectation['conducteurs'] }[] }>('/api/v1/planification/referentiel')).donnees;
      const t = r.transporteurs.find((x) => x.id === o.transporteur.id);
      return { vehicules: t?.vehicules ?? [], conducteurs: t?.conducteurs ?? [] };
    },
  });
  const [vehiculeId, setVehiculeId] = useState(o.vehicule?.id ?? '');
  const [conducteurId, setConducteurId] = useState(o.conducteur?.id ?? '');
  const maj = useMutation({
    mutationFn: async () => api(`/api/v1/ordres-transport/${o.id}/affectation`, { methode: 'PATCH', corps: { vehiculeId: vehiculeId || null, conducteurId: conducteurId || null } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['ordre-transport', o.id] }),
  });
  return (
    <section className="carte-bloc" aria-labelledby="titre-affectation">
      <h2 id="titre-affectation">Véhicule et conducteur</h2>
      {flotte.isLoading && <Squelette lignes={2} />}
      {flotte.error && <MessageErreur erreur={flotte.error} />}
      {flotte.data && (
        <form
          className="formulaire-ligne"
          onSubmit={(e) => {
            e.preventDefault();
            maj.mutate();
          }}
        >
          <div className="champ">
            <label htmlFor="a-vehicule">Véhicule</label>
            <select id="a-vehicule" value={vehiculeId} onChange={(e) => setVehiculeId(e.target.value)}>
              <option value="">À affecter</option>
              {flotte.data.vehicules.map((v) => (
                <option key={v.id} value={v.id} disabled={v.engage && v.id !== o.vehicule?.id}>
                  {v.immatriculation}, {quantite(v.capacite, 'L')}, {v.compartiments} compartiment{v.compartiments > 1 ? 's' : ''}
                  {v.engage && v.id !== o.vehicule?.id ? ' (engagé sur une autre mission)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="a-conducteur">Conducteur</label>
            <select id="a-conducteur" value={conducteurId} onChange={(e) => setConducteurId(e.target.value)}>
              <option value="">À affecter</option>
              {flotte.data.conducteurs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nom}
                </option>
              ))}
            </select>
            {flotte.data.conducteurs.length === 0 && <span className="aide">Aucun conducteur lisible avec vos habilitations</span>}
          </div>
          <div className="actions">
            <button type="submit" className="bouton primaire" disabled={maj.isPending}>
              Enregistrer l’affectation
            </button>
          </div>
          {maj.error && <Message type="erreur">{messageErreur(maj.error)}</Message>}
          {maj.isSuccess && <Message type="succes">Affectation enregistrée.</Message>}
        </form>
      )}
    </section>
  );
}

function AnnulationOrdre({ o }: { o: FicheOrdre }) {
  const client = useQueryClient();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const annuler = useMutation({
    mutationFn: async () => api<{ mouvementRetourId: string | null }>(`/api/v1/ordres-transport/${o.id}/annulation`, { corps: { motif } }),
    onSuccess: () => {
      setOuvert(false);
      void client.invalidateQueries({ queryKey: ['ordre-transport', o.id] });
      void client.invalidateQueries({ queryKey: ['suivi-missions'] });
    },
  });
  const apresChargement = o.etat !== 'emis';
  return (
    <section className="carte-bloc" aria-labelledby="titre-annulation">
      <h2 id="titre-annulation">Annuler l’ordre</h2>
      <p className="attenue">
        {apresChargement
          ? 'Le produit est déjà chargé : l’annulation crée un mouvement de retour validé au dépôt et clôt la ligne de transit (R-20).'
          : 'Avant chargement, l’annulation lève la réservation sur votre position au dépôt.'}
      </p>
      {!ouvert ? (
        <button type="button" className="bouton" onClick={() => setOuvert(true)}>
          Annuler cet ordre
        </button>
      ) : (
        <form
          className="formulaire-ligne"
          onSubmit={(e) => {
            e.preventDefault();
            annuler.mutate();
          }}
        >
          <div className="champ">
            <label htmlFor="an-motif">Motif de l’annulation</label>
            <input id="an-motif" value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} maxLength={500} />
          </div>
          <div className="actions">
            <button type="submit" className="bouton primaire" disabled={annuler.isPending || motif.trim().length < 3}>
              Confirmer l’annulation
            </button>
            <button type="button" className="bouton" onClick={() => setOuvert(false)}>
              Garder l’ordre
            </button>
          </div>
          {annuler.error && <Message type="erreur">{messageErreur(annuler.error)}</Message>}
        </form>
      )}
    </section>
  );
}

// --- Livraisons attendues et réception -------------------------------------------------------------------------

type LivraisonAttendue = NonNullable<PaquetTerrain['livraisonsAttendues']>[number];

function LivraisonsAttendues() {
  const { etat } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paquet = usePaquet();
  const enLigne = !etat?.horsLigne;
  const attendues = useQuery({
    queryKey: ['receptions-attendues'],
    queryFn: async () => (await api<(LigneOrdre & { receptionEnAttenteId: string | null })[]>('/api/v1/receptions/attendues')).donnees,
    enabled: enLigne,
    retry: false,
  });
  const [numero, setNumero] = useState('');
  const [erreurRecherche, setErreurRecherche] = useState<string | null>(null);
  const stationFiltre = params.get('station');
  const locales = (paquet.data?.contenu.livraisonsAttendues ?? []).filter((l) => !stationFiltre || l.destinationId === stationFiltre);
  const rechercher = async (e: FormEvent) => {
    e.preventDefault();
    setErreurRecherche(null);
    const n = numero.trim().toUpperCase();
    const local = locales.find((l) => l.numero === n);
    if (local) return navigate(`/livraisons/${local.id}/reception`);
    try {
      const r = (await api<{ id: string }>(`/api/v1/receptions/recherche?numero=${encodeURIComponent(n)}`)).donnees;
      navigate(`/livraisons/${r.id}/reception`);
    } catch (err) {
      setErreurRecherche(messageErreur(err));
    }
  };
  const lignes = attendues.data?.filter((l) => !stationFiltre || l.destination.id === stationFiltre);
  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Livraisons attendues</h1>
          <p className="attenue">Confirmez chaque livraison à son arrivée : quantités mesurées par compartiment, comparaison avec le chargement, écart et justificatif. La saisie fonctionne hors connexion.</p>
        </div>
      </div>
      <form className="carte-bloc formulaire-ligne" onSubmit={rechercher} aria-labelledby="titre-recherche-ordre">
        <h2 id="titre-recherche-ordre">1. Référence du bon de livraison</h2>
        <div className="champ">
          <label htmlFor="l-numero">Numéro de l’ordre de transport</label>
          <input id="l-numero" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="OT-2026-00001" pattern="[Oo][Tt]-\d{4}-\d{5}" required />
          <span className="aide">Inscrit sur le bon de livraison remis par le conducteur</span>
        </div>
        <div className="actions">
          <button type="submit" className="bouton primaire">
            Retrouver la livraison
          </button>
        </div>
        {erreurRecherche && <Message type="erreur">{erreurRecherche}</Message>}
      </form>
      <section className="carte-bloc" aria-labelledby="titre-attendues">
        <div className="carte-bloc-tete">
          <h2 id="titre-attendues">À réceptionner</h2>
          {!enLigne && <span className="attenue">Dernières données synchronisées {paquet.data?.enregistreLe ? dateHeure(paquet.data.enregistreLe) : 'à une date inconnue'}</span>}
        </div>
        {enLigne && attendues.isLoading && <Squelette lignes={3} />}
        {enLigne && attendues.error && <MessageErreur erreur={attendues.error} />}
        {lignes && lignes.length === 0 && <Vide>Aucune livraison attendue.</Vide>}
        {lignes && lignes.length > 0 && (
          <ul className="liste-lignes">
            {lignes.map((l) => (
              <li key={l.id}>
                <Link to={l.receptionEnAttenteId ? `/livraisons/${l.id}` : `/livraisons/${l.id}/reception`}>
                  <span>
                    <b>
                      {l.numero}, {volumeOrdre(l)} {deProduit(l.produitCode)} <EtiquetteEtatOrdre etat={l.etat} libelle={l.etatLibelle} />
                    </b>
                    <small>
                      {l.destination.nom}, depuis {l.origine.nom} ; {arrivee(l)} ; {l.vehicule ? `citerne ${l.vehicule.immatriculation}` : 'véhicule non communiqué'}
                      {l.receptionEnAttenteId ? ' ; réception saisie, en attente de validation' : ''}
                    </small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ul>
        )}
        {(!enLigne || attendues.isError) && (
          <>
            {locales.length === 0 ? (
              <Vide>Aucune livraison attendue dans les données de cet appareil.</Vide>
            ) : (
              <ul className="liste-lignes">
                {locales.map((l) => (
                  <li key={l.id}>
                    <Link to={`/livraisons/${l.id}/reception`}>
                      <span>
                        <b>
                          {l.numero}, {quantite(l.quantiteChargee, 'L')} {deProduit(l.produitCode)}
                        </b>
                        <small>
                          {l.destination ?? 'destination indisponible'}, arrivée prévue {dateHeure(l.dateArriveePrevue)}
                        </small>
                      </span>
                      <Icone nom="chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** Tolérance applicable (D-019) : le plus grand du pourcentage et du plancher. */
function toleranceDe(paquet: PaquetTerrain | undefined, produit: string, charge: number): number | null {
  const t = produit === 'gasoil' ? paquet?.parametresTerrain?.toleranceGasoil : produit.startsWith('super') ? paquet?.parametresTerrain?.toleranceSuper : undefined;
  if (!t) return null;
  return Math.max((charge * Number(t.pourcentage)) / 100, Number(t.plancher));
}

export function PageReception() {
  const { id } = useParams();
  const paquet = usePaquet();
  const livraison = paquet.data?.contenu.livraisonsAttendues?.find((l) => l.id === id);
  if (paquet.isLoading) return <Squelette lignes={6} libelle="Chargement de la livraison" />;
  if (!livraison)
    return (
      <Message type="avertissement">
        Cette livraison ne figure pas parmi les livraisons attendues de cet appareil (déjà soldée, destinée à une autre installation ou données non synchronisées). <Link to={`/livraisons/${id}`}>Ouvrir la fiche de l’ordre</Link>
      </Message>
    );
  return <FormulaireReception l={livraison} paquet={paquet.data?.contenu} />;
}

function FormulaireReception({ l, paquet }: { l: LivraisonAttendue; paquet: PaquetTerrain | undefined }) {
  const { synchroniseur } = useHorsLigne();
  const client = useQueryClient();
  const compartiments = l.compartiments.length ? l.compartiments : [{ numero: 1, volumePrevu: l.quantiteChargee ?? '0', volumeCharge: l.quantiteChargee }];
  const [mesures, setMesures] = useState<Record<number, string>>({});
  const [cuve, setCuve] = useState(l.cuves[0]?.id ?? '');
  const [partielle, setPartielle] = useState(false);
  const [observations, setObservations] = useState('');
  const [quand, setQuand] = useState(maintenantLocal);
  const [resultat, setResultat] = useState<string | null>(null);
  const dejaRecu = Number(l.quantiteRecue);
  const charge = Number(l.quantiteChargee ?? '0');
  const attendu = charge - dejaRecu;
  const saisi = useMemo(() => Object.values(mesures).reduce((s, v) => s + (Number(v.replace(/\s/g, '').replace(',', '.')) || 0), 0), [mesures]);
  const ecart = saisi - attendu;
  const tolerance = toleranceDe(paquet, l.produitCode, charge);
  const horsTolerance = tolerance !== null && !partielle && Math.abs(ecart) > tolerance;
  const complet = compartiments.every((c) => (mesures[c.numero] ?? '').trim() !== '');
  const envoi = useMutation({
    mutationFn: async () =>
      synchroniseur.enregistrer(
        TYPES_OPERATIONS_LOT3.confirmationReception,
        {
          ordreId: l.id,
          reservoirId: cuve,
          mesures: compartiments.map((c) => ({ compartiment: compartiments.length > 1 ? c.numero : null, quantite: (mesures[c.numero] ?? '0').replace(/\s/g, '').replace(',', '.') })),
          partielle,
          observations: observations.trim() || null,
        },
        { horodatageEvenement: new Date(`${quand}:00+01:00`), versionObjet: l.version, resume: `Réception de la livraison ${l.numero}, ${l.destination ?? ''}` },
      ),
    onSuccess: () => {
      setResultat(
        `Réception enregistrée sur cet appareil. Elle est envoyée dès que le réseau le permet ; « synchronisé » s’affichera à l’accusé du serveur.${horsTolerance ? ' L’écart dépasse la tolérance : un dossier de rapprochement sera ouvert à la validation.' : ''}${partielle ? ' Le reliquat reste en transit pendant le délai de la réception partielle.' : ''}`,
      );
      void client.invalidateQueries({ queryKey: ['receptions-attendues'] });
    },
  });
  if (l.cuves.length === 0) return <Message type="avertissement">Aucune cuve {deProduit(l.produitCode)} n’est déclarée dans cette installation : la réception ne peut pas être affectée.</Message>;
  return (
    <form
      aria-labelledby="titre-reception"
      onSubmit={(e) => {
        e.preventDefault();
        setResultat(null);
        envoi.mutate();
      }}
    >
      <div className="page-tete">
        <div>
          <span className="attenue">{l.destination ?? 'Destination indisponible'}</span>
          <h1 id="titre-reception">Réception de {l.numero}</h1>
          <p className="attenue">
            2. Livraison retrouvée : {quantite(l.quantiteChargee, 'L')} {deProduit(l.produitCode)} chargés
            {dejaRecu > 0 ? `, ${quantite(l.quantiteRecue, 'L')} déjà reçus (reliquat attendu ${quantite(String(attendu), 'L')})` : ''}, arrivée prévue {dateHeure(l.dateArriveePrevue)}. Scellés : {l.scelles.length ? l.scelles.join(', ') : 'aucun déclaré'}.
          </p>
        </div>
      </div>
      <fieldset className="carte-bloc">
        <legend>3. Quantités reçues</legend>
        <div className="formulaire-ligne">
        {compartiments.map((c) => (
          <div className="champ" key={c.numero}>
            <label htmlFor={`rc-${c.numero}`}>
              {compartiments.length > 1 ? `Compartiment ${c.numero}` : 'Quantité mesurée'} (litres)
            </label>
            <input id={`rc-${c.numero}`} inputMode="decimal" value={mesures[c.numero] ?? ''} onChange={(e) => setMesures({ ...mesures, [c.numero]: e.target.value })} required />
            <span className="aide">Chargé : {c.volumeCharge ? quantite(c.volumeCharge, 'L') : quantite(c.volumePrevu, 'L') + ' prévus'}</span>
          </div>
        ))}
        <div className="champ">
          <label htmlFor="rc-cuve">Cuve de destination</label>
          <select id="rc-cuve" value={cuve} onChange={(e) => setCuve(e.target.value)}>
            {l.cuves.map((c) => (
              <option key={c.id} value={c.id}>
                Cuve {c.numero}, {nomProduit(c.produitCode)}, capacité {quantite(c.capacite, 'L')}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="rc-quand">Heure de la mesure</label>
          <input id="rc-quand" type="datetime-local" value={quand} onChange={(e) => setQuand(e.target.value)} required />
        </div>
        </div>
      </fieldset>
      <fieldset className="carte-bloc">
        <legend>4 et 5. Comparaison et différence</legend>
        <dl className="liste-definitions">
          <dt>Attendu</dt>
          <dd>{quantite(String(attendu), 'L')}</dd>
          <dt>Mesuré</dt>
          <dd>{complet ? quantite(String(saisi), 'L') : <span className="attenue">saisissez chaque compartiment</span>}</dd>
          <dt>Écart</dt>
          <dd>{complet ? `${ecart > 0 ? '+' : ''}${quantite(String(ecart), 'L')}` : <span className="attenue">indisponible tant que la saisie est incomplète</span>}</dd>
          <dt>Tolérance</dt>
          <dd>{tolerance === null ? <span className="attenue">indisponible : paramètre non chargé sur cet appareil, le serveur l’appliquera</span> : `${quantite(String(Math.round(tolerance)), 'L')} (D-019)`}</dd>
        </dl>
        {complet && horsTolerance && <Message type="avertissement">Écart au-delà de la tolérance : un dossier de rapprochement sera ouvert automatiquement entre les parties. Décrivez ce que vous avez constaté ci-dessous.</Message>}
        <label className="case">
          <input type="checkbox" checked={partielle} onChange={(e) => setPartielle(e.target.checked)} />
          Réception partielle : le reste sera livré plus tard ({paquet?.parametresTerrain?.delaiReliquatHeures ? `le reliquat reste en transit pendant ${paquet.parametresTerrain.delaiReliquatHeures} h` : 'le reliquat reste en transit pendant le délai paramétré, indisponible sur cet appareil'}, D-020)
        </label>
      </fieldset>
      <fieldset className="carte-bloc">
        <legend>6. Observations et justificatif</legend>
        <div className="champ">
          <label htmlFor="rc-obs">Observations</label>
          <textarea id="rc-obs" value={observations} onChange={(e) => setObservations(e.target.value)} maxLength={1000} rows={3} />
          <span className="aide">État des scellés, température, incident à la livraison. Le justificatif signé se joint ensuite au dossier depuis la fiche de l’ordre.</span>
        </div>
      </fieldset>
      {envoi.error && <Message type="erreur">{messageErreur(envoi.error)}</Message>}
      {resultat && <Message type="succes">{resultat}</Message>}
      <div className="actions">
        <button type="submit" className="bouton primaire large" disabled={envoi.isPending || !complet || !cuve || Boolean(resultat)}>
          7. Enregistrer la réception
        </button>
        <Link className="bouton" to="/livraisons">
          Retour aux livraisons
        </Link>
      </div>
      <p className="attenue">8. À la validation par une personne habilitée, le stock de la cuve est mis à jour et la ligne de transit est close.</p>
    </form>
  );
}
